import { ReactNode, useState } from 'react';

/**
 * A country flag as a plain <img> served from the flag-icons CDN (jsDelivr), rather than a
 * React library. `react-world-flags` inlined all 256 flags as data-URIs into a single
 * non-tree-shakeable UMD bundle (~1.2 MB gzipped) that shipped in the app's JS; this pulls
 * each flag as an independently-cached SVG only when it is actually rendered, for ~0 JS cost.
 * We are proxied through Cloudflare, so the SVGs edge-cache without a third-party dependency
 * on our own render path once mirrored — see `docs/` if we later self-host under /public.
 *
 * Decorative by default (aria-hidden, empty alt): every call site shows the country/currency
 * name in adjacent text. Renders `fallback` for an unknown or non-ISO code (e.g. a
 * currency with no mapped country) instead of a broken image.
 */
const FLAG_CDN_BASE = 'https://cdn.jsdelivr.net/gh/lipis/flag-icons@7/flags/4x3';

const ISO_ALPHA_2 = /^[a-z]{2}$/;

interface Props {
  code: string;
  className?: string;
  fallback?: ReactNode;
}

export default function CountryFlag({ code, className, fallback = null }: Props) {
  const [failed, setFailed] = useState(false);
  const normalized = code?.trim().toLowerCase();

  if (!normalized || !ISO_ALPHA_2.test(normalized) || failed) {
    return <>{fallback}</>;
  }

  return (
    <img
      src={`${FLAG_CDN_BASE}/${normalized}.svg`}
      alt=""
      aria-hidden="true"
      loading="lazy"
      className={className}
      onError={() => setFailed(true)}
    />
  );
}
